import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, buildWhatsAppUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, card, btnPrimary, inp, label } from "./AdminShell";
import { MessageSquare, Copy, ChevronRight, X, Trash2 } from "lucide-react";

const STATUSES = ["Baru", "Dihubungi", "Diproses", "Selesai"];
const statusColor = (s) => ({ "Baru": "bg-[#FF9418]/10 text-[#FF9418]", "Dihubungi": "bg-blue-50 text-blue-700", "Diproses": "bg-purple-50 text-purple-700", "Selesai": "bg-green-50 text-green-700" }[s] || "bg-neutral-100 text-neutral-600");
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

function useEnquiryList(type) {
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const refresh = () => {
    setLoading(true);
    const params = { type };
    if (statusFilter) params.status = statusFilter;
    api.get("/admin/enquiries", { params }).then((r) => setList(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, [type, statusFilter]);
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/enquiries/${id}`, { status }); toast.success("Enquiry status diperbarui."); refresh(); }
    catch { toast.error("Gagal memperbarui"); }
  };
  const saveNotes = async (id, internal_notes) => {
    try { await api.patch(`/admin/enquiries/${id}`, { internal_notes }); toast.success("Catatan disimpan."); refresh(); }
    catch { toast.error("Gagal menyimpan catatan"); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus enquiry ini?")) return;
    try { await api.delete(`/admin/enquiries/${id}`); toast.success("Enquiry dihapus."); setActive(null); refresh(); }
    catch { toast.error("Gagal menghapus"); }
  };
  return { list, statusFilter, setStatusFilter, loading, active, setActive, setStatus, saveNotes, del, refresh };
}

function StatusFilter({ value, onChange }) {
  return (
    <div className="flex gap-1 bg-neutral-100 p-1 rounded-md w-fit mb-4">
      {[["", "Semua"], ...STATUSES.map((s) => [s, s])].map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)}
          className={`px-3 py-1.5 text-xs font-semibold rounded ${value === v ? "bg-white text-[#FF9418] shadow-sm" : "text-neutral-600"}`}
          data-testid={`enq-status-${v || "all"}`}>{l}</button>
      ))}
    </div>
  );
}

function EnquiryDetailPanel({ enq, columns, replyMessage, onClose, onSetStatus, onSaveNotes, onDelete }) {
  const [notes, setNotes] = useState(enq.internal_notes || "");
  useEffect(() => { setNotes(enq.internal_notes || ""); }, [enq.id]);
  const waUrl = buildWhatsAppUrl(enq.whatsapp, replyMessage(enq));
  const copyWa = () => { navigator.clipboard.writeText(enq.whatsapp); toast.success("Nomor WhatsApp disalin."); };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-neutral-200 p-4 flex items-center justify-between z-10">
          <div>
            <div className={`inline-block px-2 py-0.5 rounded text-[11px] font-mono uppercase ${statusColor(enq.status)}`}>{enq.status}</div>
            <h2 className="font-display font-bold text-neutral-900 text-lg mt-1">{enq.name}</h2>
            <div className="text-xs text-neutral-500">{fmtDate(enq.created_at)}</div>
          </div>
          <button onClick={onClose} className="text-neutral-500"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Fields */}
          <div className={`${card} p-4 grid grid-cols-2 gap-3 text-sm`}>
            {columns.map(({ k, l }) => enq[k] ? (
              <div key={k} className={k === "message" || k === "description" ? "col-span-2" : ""}>
                <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-0.5">{l}</div>
                <div className="text-neutral-900 whitespace-pre-wrap">{enq[k]}</div>
              </div>
            ) : null)}
          </div>

          {/* Attachments (spare_part) */}
          {enq.attachments?.length > 0 && (
            <div className={`${card} p-4`}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">Foto Lampiran</div>
              <div className="grid grid-cols-3 gap-2">
                {enq.attachments.map((a, i) => {
                  const src = a.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${a}` : a;
                  return <a key={i} href={src} target="_blank" rel="noreferrer"><img src={src} className="w-full aspect-square object-cover border border-neutral-200 rounded-md hover:opacity-80" alt="" /></a>;
                })}
              </div>
            </div>
          )}

          {/* WhatsApp CTA */}
          {enq.whatsapp && (
            <div className={`${card} p-4`}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">Balas via WhatsApp</div>
              <div className="text-sm text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-md p-3 mb-3 whitespace-pre-wrap">{replyMessage(enq)}</div>
              <div className="flex gap-2">
                <a href={waUrl} target="_blank" rel="noreferrer" className="flex-1"><Button className="bg-green-600 hover:bg-green-700 text-white w-full h-10" data-testid="reply-wa"><MessageSquare size={14} /> Buka WhatsApp</Button></a>
                <Button onClick={copyWa} variant="outline" className="border-neutral-300 h-10"><Copy size={14} /> Copy No.</Button>
              </div>
            </div>
          )}

          {/* Status */}
          <div className={`${card} p-4`}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">Ubah Status</div>
            <div className="flex gap-1 flex-wrap">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => onSetStatus(enq.id, s)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded ${enq.status === s ? statusColor(s) + " ring-1 ring-current" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}
                  data-testid={`set-status-${s}`}>{s}</button>
              ))}
            </div>
          </div>

          {/* Internal notes */}
          <div className={`${card} p-4`}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">Catatan Internal</div>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={inp} placeholder="Catatan follow-up, harga yang ditawarkan, dll..." />
            <Button onClick={() => onSaveNotes(enq.id, notes)} className={`${btnPrimary} h-9 mt-2`} data-testid="save-notes">Simpan Catatan</Button>
          </div>

          <Button onClick={() => onDelete(enq.id)} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 w-full h-10"><Trash2 size={14} /> Hapus Enquiry</Button>
        </div>
      </div>
    </div>
  );
}

function EnquiryTable({ list, loading, columns, onOpen }) {
  return (
    <div className={`${card} overflow-hidden`}>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
            <tr>{columns.map((c) => <th key={c.k} className="text-left px-4 py-3">{c.l}</th>)}<th className="text-left px-4 py-3">Status</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {loading ? <tr><td colSpan={columns.length + 2} className="text-center py-8 text-neutral-500">Memuat...</td></tr> :
              list.length === 0 ? <tr><td colSpan={columns.length + 2} className="text-center py-8 text-neutral-500">Belum ada enquiry.</td></tr> :
                list.map((e) => (
                  <tr key={e.id} onClick={() => onOpen(e)} className="hover:bg-neutral-50 cursor-pointer" data-testid={`enq-row-${e.id}`}>
                    {columns.map((c) => (
                      <td key={c.k} className="px-4 py-3 text-neutral-900">
                        {c.k === "created_at" ? <span className="text-neutral-500 whitespace-nowrap">{fmtDate(e[c.k])}</span> :
                          c.k === "name" ? <span className="font-semibold">{e[c.k]}</span> :
                          <span className="text-neutral-700">{e[c.k] || "—"}</span>}
                      </td>
                    ))}
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-[11px] font-mono uppercase ${statusColor(e.status)}`}>{e.status}</span></td>
                    <td className="px-4 py-3 text-right pr-4"><ChevronRight size={14} className="text-neutral-400 inline" /></td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-neutral-100">
        {list.map((e) => (
          <div key={e.id} onClick={() => onOpen(e)} className="p-4 cursor-pointer" data-testid={`enq-card-${e.id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-neutral-900">{e.name}</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase ${statusColor(e.status)}`}>{e.status}</span>
            </div>
            <div className="text-xs text-neutral-500">{e.whatsapp} · {fmtDate(e.created_at)}</div>
            <div className="text-xs text-neutral-600 mt-1 truncate">{e.product_name || e.subject || e.part_name || ""}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* === Product Enquiries === */
export function AdminEnquiriesProduct() {
  const state = useEnquiryList("product");
  const columns = [
    { k: "created_at", l: "Tanggal" }, { k: "name", l: "Customer" }, { k: "whatsapp", l: "WhatsApp" },
    { k: "product_name", l: "Produk" }, { k: "city", l: "Kota" }, { k: "company", l: "Perusahaan" },
    { k: "email", l: "Email" }, { k: "message", l: "Pesan" },
  ];
  const detailColumns = [
    { k: "whatsapp", l: "WhatsApp" }, { k: "email", l: "Email" }, { k: "company", l: "Perusahaan" },
    { k: "city", l: "Kota" }, { k: "product_name", l: "Produk" }, { k: "message", l: "Pesan" },
  ];
  const reply = (e) => `Halo ${e.name}, terima kasih atas ketertarikannya pada ${e.product_name || "produk kami"} di teknikdiesel.com. Berikut informasi harga, kondisi, dan ketersediaan unit yang Anda tanyakan:`;
  return (
    <div>
      <PageHeader title="Product Enquiries" subtitle="Permintaan penawaran produk dari website" />
      <StatusFilter value={state.statusFilter} onChange={state.setStatusFilter} />
      <EnquiryTable list={state.list} loading={state.loading} columns={columns.slice(0, 6)} onOpen={state.setActive} />
      {state.active && <EnquiryDetailPanel enq={state.active} columns={detailColumns} replyMessage={reply} onClose={() => state.setActive(null)} onSetStatus={state.setStatus} onSaveNotes={state.saveNotes} onDelete={state.del} />}
    </div>
  );
}

/* === Spare Part Enquiries === */
export function AdminEnquiriesSparePart() {
  const state = useEnquiryList("spare_part");
  const columns = [
    { k: "created_at", l: "Tanggal" }, { k: "name", l: "Customer" }, { k: "whatsapp", l: "WhatsApp" },
    { k: "brand", l: "Brand Mesin" }, { k: "machine_model", l: "Model" }, { k: "part_name", l: "Part" },
  ];
  const detailColumns = [
    { k: "whatsapp", l: "WhatsApp" }, { k: "brand", l: "Brand Mesin" }, { k: "machine_model", l: "Model Mesin" },
    { k: "part_name", l: "Nama Part" }, { k: "part_number", l: "Part Number" }, { k: "description", l: "Deskripsi" },
  ];
  const reply = (e) => `Halo ${e.name}, terima kasih atas permintaan spare part untuk ${e.brand} ${e.machine_model || ""}. Kami akan cek ketersediaan ${e.part_name} dan segera informasikan.`;
  return (
    <div>
      <PageHeader title="Spare Parts Enquiries" subtitle="Permintaan cari spare part dari customer" />
      <StatusFilter value={state.statusFilter} onChange={state.setStatusFilter} />
      <EnquiryTable list={state.list} loading={state.loading} columns={columns} onOpen={state.setActive} />
      {state.active && <EnquiryDetailPanel enq={state.active} columns={detailColumns} replyMessage={reply} onClose={() => state.setActive(null)} onSetStatus={state.setStatus} onSaveNotes={state.saveNotes} onDelete={state.del} />}
    </div>
  );
}

/* === Contact Enquiries === */
export function AdminEnquiriesContact() {
  const state = useEnquiryList("contact");
  const columns = [
    { k: "created_at", l: "Tanggal" }, { k: "name", l: "Customer" }, { k: "whatsapp", l: "WhatsApp" },
    { k: "subject", l: "Subject" }, { k: "city", l: "Kota" }, { k: "company", l: "Perusahaan" },
  ];
  const detailColumns = [
    { k: "whatsapp", l: "WhatsApp" }, { k: "email", l: "Email" }, { k: "company", l: "Perusahaan" },
    { k: "city", l: "Kota" }, { k: "subject", l: "Subject" }, { k: "message", l: "Pesan" },
  ];
  const reply = (e) => `Halo ${e.name}, terima kasih telah menghubungi Teknik Diesel Indonesia. Mengenai pertanyaan Anda${e.subject ? ` terkait "${e.subject}"` : ""}, berikut jawaban kami:`;
  return (
    <div>
      <PageHeader title="Contact Enquiries" subtitle="Pesan dari formulir kontak website" />
      <StatusFilter value={state.statusFilter} onChange={state.setStatusFilter} />
      <EnquiryTable list={state.list} loading={state.loading} columns={columns} onOpen={state.setActive} />
      {state.active && <EnquiryDetailPanel enq={state.active} columns={detailColumns} replyMessage={reply} onClose={() => state.setActive(null)} onSetStatus={state.setStatus} onSaveNotes={state.saveNotes} onDelete={state.del} />}
    </div>
  );
}
